import Vue from 'vue'
import VueRouter from 'vue-router'

import ImportCredential from '@/components/ImportCredential.vue'
import { createPopupAuth } from '@/services/auth/popupAuth.js'
import PermissionDenied from '@/views/PermissionDenied'
import RdssError from '@/views/RdssError'
import CreateOrUpdateCredential from '@/views/credentials/CreateOrUpdateCredential'
import CredentialCertificates from '@/views/credentials/CredentialCertificates'
import Credentials from '@/views/credentials/Credentials'
import CredentialsAdmin from '@/views/credentials/CredentialsAdmin'
import CredentialsEvroAdmin from '@/views/credentials/CredentialsEvroAdmin'
import ImportEvro from '@/views/credentials/ImportEvro'
import ImportedCredentialsAdmin from '@/views/credentials/ImportedCredentialsAdmin'
import Dashboard from '@/views/dashboard/Dashboard'
import AddDevice from '@/views/devices/AddDevice'
import UpdateDevice from '@/views/devices/UpdateDevice'
import License from '@/views/license/License'
import DocumentSignatureCustomization from '@/views/signature/DocumentSignatureCustomization'
import MultiSignatureCustomization from '@/views/signature/MultiSignatureCustomization'
import SignatureBackground from '@/views/signature/SignatureBackground'
import AllSignatureChains from '@/views/signature/chains/AllSignatureChains'
import AsicSignatureCustomization from '@/views/signature/chains/AsicSignatureCustomization'
import RecycleBinSignatureChains from '@/views/signature/chains/RecycleBinSignatureChains'
import SignatureChains from '@/views/signature/chains/SignatureChains'
import UploadDocumentWithChain from '@/views/signature/chains/UploadDocumentWithChain.vue'
import CreateOrUpdateSignatureProfile from '@/views/signature/profiles/CreateOrUpdateSignatureProfile'
import SignatureProfiles from '@/views/signature/profiles/SignatureProfiles'
import CreateOrUpdateUser from '@/views/users/CreateOrUpdateUser'
import UserProfile from '@/views/users/UserProfile'
import Users from '@/views/users/Users'
import UserGroups from '@/views/users/groups/UserGroups.vue'
import Welcome from '@/views/welcome/Welcome'
import { UserManager, WebStorageStateStore } from 'oidc-client-ts'
import Role from '../Role'
import users from '../services/api/users'
import store from '../store'
import ApproveCustomization from '../views/signature/ApproveCustomization.vue'
import ClerkModeSignatureCustomization from '../views/signature/ClerkModeSignatureCustomization.vue'
import QuantumSafeDemo from '../views/signature/QuantumSafeDemo.vue'
import SetSignatureFields from '../views/signature/chains/SetSignatureFields.vue'
import CreateChainFromExternal from '../views/signature/chains/integrations/CreateChainFromExternal.vue'

Vue.use(VueRouter)

const routes = [
  {
    path: '/welcome',
    name: 'Welcome',
    component: Welcome,
    meta: {
      layout: 'blank'
    }
  },
  {
    path: '/',
    redirect: 'Welcome'
  },
  {
    path: '/dashboard',
    name: 'Dashboard',
    component: Dashboard,
    meta: {
      allowedRoles: [Role.ADMIN, Role.USER]
    }
  },
  {
    path: '/importedCredentials',
    name: 'ImportedCredentialsAdmin',
    component: ImportedCredentialsAdmin,
    meta: {
      allowedRoles: [Role.ADMIN]
    }
  },
  {
    path: '/importedEvroTrustCredentials',
    name: 'CredentialsEvroAdmin',
    component: CredentialsEvroAdmin,
    meta: {
      allowedRoles: [Role.ADMIN]
    }
  },
  {
    path: '/credentialsAdmin',
    name: 'CredentialsAdmin',
    component: CredentialsAdmin,
    meta: {
      allowedRoles: [Role.ADMIN]
    }
  },
  {
    path: '/users',
    name: 'Users',
    component: Users,
    meta: {
      allowedRoles: [Role.ADMIN]
    }
  },
  {
    path: '/users-groups',
    name: 'UserGroups',
    component: UserGroups,
    meta: {
      allowedRoles: [Role.ADMIN]
    }
  },
  {
    path: '/signature-background',
    name: 'SignatureBackground',
    component: SignatureBackground,
    meta: {
      allowedRoles: [Role.ADMIN]
    }
  },
  {
    path: '/signatureChains/list/:filter?/',
    // if ends with "?q=jiraId-123" it will search for it
    // unfortunetly cant declare here in the routes array
    name: 'SignatureChains',
    meta: {
      allowedRoles: [Role.USER]
    },
    component: SignatureChains,
    children: [
      {
        name: 'UploadDocumentWithChain',
        path: '/signatureChains/upload',
        components: {
          default: SignatureChains,
          dialog: UploadDocumentWithChain
        },
        meta: {
          allowedRoles: [Role.USER]
        }
      }
    ]
  },
  {
    name: 'AllSignatureChains',
    path: '/signatureChains/administrator/list',
    component: AllSignatureChains,
    meta: {
      allowedRoles: [Role.ADMIN]
    }
  },
  {
    name: 'RecycleBinSignatureChains',
    path: '/signatureChains/administrator/recycleBin',
    component: RecycleBinSignatureChains,
    meta: {
      allowedRoles: [Role.ADMIN]
    }
  },
  {
    path: '/signatureProfiles',
    name: 'SignatureProfiles',
    component: SignatureProfiles,
    meta: {
      allowedRoles: [Role.USER]
    },
    children: [
      {
        path: '/signatureProfiles/add',
        name: 'CreateSignatureProfile',
        components: { dialog: CreateOrUpdateSignatureProfile },
        meta: {
          allowedRoles: [Role.USER]
        }
      },
      {
        path: '/signatureProfiles/:signatureProfileId/update',
        name: 'UpdateSignatureProfile',
        components: { dialog: CreateOrUpdateSignatureProfile },
        meta: {
          allowedRoles: [Role.USER]
        }
      }
    ]
  },
  {
    path: '/document/:docId/clerk/:signatureId?/:currentSignatureId?',
    name: 'ClerkModeSignatureCustomization',
    component: ClerkModeSignatureCustomization,
    meta: {
      allowedRoles: [Role.USER]
    }
  },
  {
    path: '/document/:chainId/:docId/customization/:usage/:signatureId?/:currentSignatureId?',
    name: 'DocumentSignatureCustomization',
    component: DocumentSignatureCustomization,
    meta: {
      allowedRoles: [Role.USER]
    }
  },
  {
    path: '/document/:docId/approve-customization/:signatureId?/:currentSignatureId?',
    name: 'ApproveCustomization',
    component: ApproveCustomization,
    meta: {
      allowedRoles: [Role.USER]
    }
  },
  {
    path: '/document/multi/customization/',
    name: 'MultiSignatureCustomization',
    component: MultiSignatureCustomization,
    meta: {
      allowedRoles: [Role.USER]
    }
  },
  {
    path: '/document/asic/customization/',
    name: 'AsicSignatureCustomization',
    component: AsicSignatureCustomization,
    meta: {
      allowedRoles: [Role.USER]
    }
  },
  {
    path: '/document/:docId/external/:externalId',
    name: 'SignatureChainFromExternal',
    component: CreateChainFromExternal,
    meta: {
      allowedRoles: [Role.USER]
    }
  },
  {
    path: '/document/:docId/create/',
    name: 'SetSignatureFields',
    component: SetSignatureFields,
    meta: {
      allowedRoles: [Role.USER]
    }
  },

  {
    path: '/credentials',
    name: 'Credentials',
    component: Credentials,
    meta: {
      allowedRoles: [Role.ADMIN, Role.USER]
    },
    children: [
      {
        path: '/credentials/:userId?/add',
        name: 'CreateCredential',
        components: { dialog: CreateOrUpdateCredential },
        meta: {
          allowedRoles: [Role.ADMIN, Role.USER]
        }
      },
      {
        path: '/credentials/:userId?/:credentialId/update',
        name: 'UpdateCredential',
        components: { dialog: CreateOrUpdateCredential },
        meta: {
          allowedRoles: [Role.ADMIN, Role.USER]
        }
      },
      {
        path: '/credentials/importCredential',
        name: 'ImportCredential',
        components: { dialog: ImportCredential },
        meta: {
          allowedRoles: [Role.USER]
        }
      },
      {
        path: '/credentials/importEvroTrust',
        name: 'ImportEvro',
        components: { dialog: ImportEvro },
        meta: {
          allowedRoles: [Role.ADMIN, Role.USER]
        }
      }
    ]
  },
  {
    path: '/credentials/:userId?/:credentialId/certificates',
    name: 'CredentialCertificates',
    component: CredentialCertificates,
    meta: {
      allowedRoles: [Role.ADMIN, Role.USER]
    }
  },
  {
    path: '/devices-admin',
    beforeEnter() {
      const rsspPublicUrl = store.state.config !== null ? store.state.remoteConfig.rsspPublicUrl : ''
      location.href = rsspPublicUrl + 'devices'
    },
    meta: {
      layout: 'blank'
    }
  },
  {
    path: '/users-rssp',
    beforeEnter() {
      const rsspPublicUrl = store.state.config !== null ? store.state.remoteConfig.rsspPublicUrl : ''
      location.href = rsspPublicUrl + 'users'
    },
    meta: {
      layout: 'blank'
    }
  },
  /* {
    path: '/user/profile',
    name: 'UserProfile',
    beforeEnter() {
      const rsspPublicUrl = store.state.config !== null ? store.state.remoteConfig.rsspPublicUrl : ''
      location.href = rsspPublicUrl + 'user/profile'
    },
    meta: {
      layout: 'blank'
    }
  }, */
  {
    path: '/user/profile',
    name: 'UserProfile',
    component: UserProfile,
    meta: {
      allowedRoles: [Role.ADMIN, Role.USER]
    },
    children: [
      {
        path: '/user/profile/:userId/update',
        name: 'ProfileUpdateUser',
        components: { dialog: CreateOrUpdateUser },
        meta: {
          allowedRoles: [Role.ADMIN, Role.USER]
        }
      },
      {
        path: '/user/profile/devices/:deviceId/update',
        name: 'ProfileUpdateDevice',
        components: { dialog: UpdateDevice },
        meta: {
          allowedRoles: [Role.USER, Role.USER]
        }
      },
      {
        path: '/user/profile/devices/add',
        name: 'ProfileAddDevice',
        components: { dialog: AddDevice },
        meta: {
          allowedRoles: [Role.USER]
        }
      }
    ]
  },
  {
    path: '/license',
    name: 'License',
    component: License,
    meta: {
      allowedRoles: [Role.ADMIN]
    }
  },
  {
    path: '/quantum-safe-demo',
    name: 'Quantum-safe Demo',
    component: QuantumSafeDemo,
    meta: {
      allowedRoles: [Role.USER]
    }
  },
  {
    path: '/permissionDenied',
    name: 'PermissionDenied',
    component: PermissionDenied,
    meta: {
      layout: 'blank'
    }
  },
  {
    path: '/notFound',
    name: 'NotFound',
    component: PermissionDenied,
    meta: {
      layout: 'blank'
    }
  },
  {
    path: '/logoutfromrssp'
  },
  {
    path: '/login-callback'
  },
  {
    path: '/popup-callback'
  },
  {
    path: '/popuplogout-callback'
  },
  {
    path: '/logout'
  },
  {
    path: '*',
    redirect: 'NotFound'
  }
]

function createRouter(auth, popupAuth) {
  const router = new VueRouter({
    mode: 'history',
    base: process.env.BASE_URL,
    routes,
    scrollBehavior(to, from, savedPosition) {
      return { top: 0, left: 0 }
    }
  }
  )

  router.beforeEach(async (to, from, next) => {
    if (to.path === '/login-callback') {
      let state = null
      try {
        state = await auth.handleLoginCallback()

        const currentUser = await users.joinUser()

        store.commit('setCurrentUser', currentUser)
        // check that we are redirecting to a path or a named route
        const isStateString = typeof state === 'string' || state instanceof String
        // if (isStateString && state.includes('/')) {
        if (isStateString) {
          next({
            path: state,
            replace: true
          })
        } else {
          next({
            // name: state,
            name: state.name,
            replace: true
          })
        }
      } catch (error) {
        console.error('rdss logincallback exception', error)
        const errorcode = error.response?.data?.code?.toUpperCase()
        if (errorcode === RdssError.USER_SUSPENDED) {
          const suspendedUser = {
            status: 'SUSPENDED'
          }
          store.commit('setCurrentUser', suspendedUser)
          next({ name: 'PermissionDenied' })
        } else {
          next({ name: 'Welcome' })
        }
      }

      return
    } else
    if (to.path === '/popup-callback') {
      const state = null
      try {
        const state = await popupAuth.handleLoginCallback()
        // window.close()
        next()
      } catch (error) {
        console.error('rdss popup-callback exception', error)
        next({ name: 'Welcome' })
      }

      return
    } else
    if (to.path === '/popuplogout-callback') {
      try {
        const state = await popupAuth.handlePopupLogoutCallback()
        next()
      } catch (error) {
        console.error('rdss popuplogout-callback exception', error)
        next({ name: 'Welcome' })
      }

      return
    }

    const user = await auth.getUser()

    if (to.name === 'Welcome') {
      if (user) {
        next({ name: 'Dashboard' })
      } else {
        next()
      }

      return
    }

    if (to.path === '/logoutfromrssp') {
      try {
        await auth.logout(false)
        const rsspPublicUrl = store.state.remoteConfig && store.state.remoteConfig.rsspPublicUrl
        if ((rsspPublicUrl !== undefined) && (rsspPublicUrl.length > 0)) {
          const logouturl = rsspPublicUrl + 'logout'
          window.location.href = logouturl
        }
      } catch (error) {
        console.error('RDSS logout exception', error)
      }

      return
    }
    if (to.path === '/logout') {
      try {
        await auth.logout()
        // store.commit('setCurrentUser', null)  //addUserUnloaded elvileg meghívja
      } catch (error) {
        console.error('RDSS logout exception', error)
        next({ name: 'Welcome' })
      }

      return
    }

    if (user) {
      const { allowedRoles } = to.meta
      if (allowedRoles) {
        let matchedRoles = []
        try {
          if (store.state.currentUser && store.state.currentUser.status !== 'SUSPENDED') {
            matchedRoles = store.state.currentUser.roles.filter(role => allowedRoles.includes(role))
          }
        } catch (error) {
          console.error(error)
          next({ name: 'PermissionDenied' })
          return
        }
        if (!matchedRoles.length) {
          next({ name: 'PermissionDenied' })
          return
        }
      }
    }

    if (!user && to.name !== 'Welcome') {
      // next({ name: 'Welcome', query: { redirect: to.fullPath } })
      await auth.login(to.fullPath)
      return
    }

    next()
  })

  auth.addUserUnloaded(() => {
    router.push({ name: 'Welcome' })
  })

  return router
}

export default createRouter
